import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Button from "../../common/Button";
import Loading from "../../common/Loading";
import {
  faArrowLeft,
  faEdit,
  faEye,
  faEyeSlash,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import LanguageSelector from "../../common/LanguageSelector";
import VideoInfoSection from "./VideoInfoSection";
import SlidesPreviewSection from "./SlidesPreviewSection";
import APIManager from "../../../managers/APIManager";

const AIVideoDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { id: videoId } = params;

  const [loading, setLoading] = useState(true);
  const [publishLoading, setPublishLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [videoData, setVideoData] = useState(null);

  const siteVirgulId = useSelector(
    (state) => state.site?.selectedSite?.virgulId
  );
  const videoCreation = useSelector(
    (state) => state.site?.selectedSite?.videoCreation
  );

  useEffect(() => {
    if (!videoCreation || !siteVirgulId) {
      setLoading(false);
      navigate("/");
      return;
    }

    const fetchVideoDetails = async () => {
      setLoading(true);
      try {
        const response = await APIManager.getAIVideo({
          siteVirgulId,
          videoId,
        });
        setVideoData(response.data);
      } catch (error) {
        console.error("Error fetching video details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideoDetails();
  }, [videoCreation, siteVirgulId, videoId, navigate]);

  if (loading) {
    return <Loading />;
  }

  if (!videoData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-500 mb-4">{t("video not found")}</p>
          <Button onClick={() => navigate("/ai-video")} variant="secondary">
            {t("back to videos")}
          </Button>
        </div>
      </div>
    );
  }

  // Convert videoData to format needed by create page
  const convertVideoDataToCreateFormat = () => {
    if (!videoData?.creationConfig) {
      return null;
    }

    const { creationConfig, template } = videoData;
    const slideItems = creationConfig.slideItems || [];
    const openingSlide = creationConfig.openingSlide || {};

    // Convert slideItems back to news items format
    const newsItems = slideItems.map((slideItem, index) => {
      const newsItem = {
        id: `edit-${videoId}-${index + 1}`,
        title: slideItem.title?.text || slideItem.title || "",
        subTitle: slideItem.subTitle?.text || slideItem.subTitle || "",
        category: slideItem.category?.text || slideItem.category || "",
        voiceText: slideItem.voiceText || "",
        image: slideItem.imageUrl || "",
      };
      return newsItem;
    });

    // Extract style settings from slideItems and openingSlide
    const styleSettings = {
      openingSlide: {
        logo: openingSlide.logo
          ? { url: openingSlide.logo }
          : { url: "" },
        backgroundColor: openingSlide.backgroundColor || "#ffffff",
      },
      slideItem: slideItems.length > 0
        ? {
          title: {
            color: slideItems[0].title?.color || "#000000",
            font: slideItems[0].title?.font || "",
            backgroundColor:
              slideItems[0].title?.backgroundColor || "#ffffff",
          },
          subTitle: {
            color: slideItems[0].subTitle?.color || "#000000",
            font: slideItems[0].subTitle?.font || "",
            backgroundColor:
              slideItems[0].subTitle?.backgroundColor || "#ffffff",
          },
          category: {
            color: slideItems[0].category?.color || "#000000",
            font: slideItems[0].category?.font || "",
            backgroundColor:
              slideItems[0].category?.backgroundColor || "#ffffff",
          },
        }
        : {
          title: {
            color: "#000000",
            font: "",
            backgroundColor: "#ffffff",
          },
          subTitle: {
            color: "#000000",
            font: "",
            backgroundColor: "#ffffff",
          },
          category: {
            color: "#000000",
            font: "",
            backgroundColor: "#ffffff",
          },
        },
    };

    return {
      initialNewsList: newsItems,
      initialSelectedNews: newsItems.map((item) => item.id),
      initialStyleSettings: styleSettings,
      initialTemplate: template,
    };
  };

  const handleEdit = () => {
    const createData = convertVideoDataToCreateFormat();
    if (createData) {
      navigate("/ai-video/new", { state: createData });
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="mx-auto max-w-7xl">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <Button
                className="mr-4"
                variant="secondary"
                icon={faArrowLeft}
                onClick={() => navigate(-1)}
              >
                {t("back")}
              </Button>
              <h1 className="text-3xl font-bold text-gray-900">
                {t("video details")}
              </h1>
            </div>
            <div className="flex items-center">
              <Button
                className="mr-4"
                variant="secondary"
                icon={faEdit}
                onClick={handleEdit}
              >
                {t("edit video")}
              </Button>
              <Button
                className="mr-4"
                color="danger"
                icon={faTrash}
                disabled={videoData.isPublished}
                loading={deleteLoading}
                onClick={() => {
                  setDeleteLoading(true);
                  APIManager.deleteAIVideo({
                    siteVirgulId,
                    videoId,
                  })
                    .then(() => {
                      navigate("/ai-video");
                    })
                    .catch(() => { })
                    .finally(() => {
                      setDeleteLoading(false);
                    });
                }}
              >
                {t("delete video")}
              </Button>
              <Button
                className="mr-4"
                color={videoData.isPublished ? "danger" : "success"}
                icon={videoData.isPublished ? faEyeSlash : faEye}
                loading={publishLoading}
                disabled={videoData.progress !== "1.00"}
                onClick={() => {
                  setPublishLoading(true);
                  if (videoData.isPublished) {
                    APIManager.unpublishAIVideo({
                      siteVirgulId,
                      videoId,
                    })
                      .then(() => {
                        setVideoData({ ...videoData, isPublished: false });
                      })
                      .catch(() => { })
                      .finally(() => {
                        setPublishLoading(false);
                      });
                  } else {
                    APIManager.publishAIVideo({
                      siteVirgulId,
                      videoId,
                    })
                      .then(() => {
                        setVideoData({ ...videoData, isPublished: true });
                      })
                      .catch(() => { })
                      .finally(() => {
                        setPublishLoading(false);
                      });
                  }
                }}
              >
                {t(videoData.isPublished ? "unpublish video" : "publish video")}
              </Button>
              <LanguageSelector />
            </div>
          </div>

          <VideoInfoSection videoData={videoData} />
          <SlidesPreviewSection videoData={videoData} />
        </div>
      </div>
    </div>
  );
};

export default AIVideoDetails;
